import { Controller } from "@hotwired/stimulus";

// Calendar popover replacing AirDatepicker's hardcoded-per-leg-index inline
// mode. One controller instance per date field group:
//   - mode "range": one visible input showing "Utresa - Hemresa", writes two
//     hidden fields (outbound + return), used by the return-trip searchfield.
//   - mode "single": one visible input, one hidden field, used by oneway and
//     each multi-city leg.
// Posted date format is always yyyy-mm-dd (matches the field the backend/
// F-ENGINE contract expects), independent of how the visible input displays.
export default class extends Controller {
  static targets = ["input", "popover", "monthLabel", "weekdays", "grid", "outboundField", "returnField", "dateField"];
  static values = {
    mode: { type: String, default: "single" },
    minDate: String,
    maxDate: String,
    defaultDate: String,
    defaultReturnDate: String,
    locale: { type: String, default: "sv-SE" },
  };

  connect() {
    const today = new Date();
    this.selectionStart = this.defaultDateValue ? this.parseDate(this.defaultDateValue) : null;
    this.selectionEnd =
      this.mode === "range" && this.defaultReturnDateValue ? this.parseDate(this.defaultReturnDateValue) : null;
    this.viewDate = this.selectionStart ? new Date(this.selectionStart) : today;
    this.pickingEnd = false;

    this.boundClose = this.closeOnOutsideClick.bind(this);
    document.addEventListener("click", this.boundClose);

    if (this.selectionStart) this.writeFields();
    this.updateInputText();
  }

  disconnect() {
    document.removeEventListener("click", this.boundClose);
  }

  get mode() {
    return this.modeValue;
  }

  parseDate(str) {
    const [y, m, d] = str.split("-").map(Number);
    return new Date(y, m - 1, d);
  }

  formatISO(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  formatDisplay(date) {
    return new Intl.DateTimeFormat(this.localeValue, { day: "numeric", month: "short" }).format(date);
  }

  toggle(event) {
    event.stopPropagation();
    this.popoverTarget.classList.toggle("hidden");
    if (!this.popoverTarget.classList.contains("hidden")) this.render();
  }

  close() {
    this.popoverTarget.classList.add("hidden");
  }

  closeOnOutsideClick(event) {
    if (!this.element.contains(event.target)) this.close();
  }

  prevMonth(event) {
    event.stopPropagation();
    this.viewDate = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() - 1, 1);
    this.render();
  }

  nextMonth(event) {
    event.stopPropagation();
    this.viewDate = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() + 1, 1);
    this.render();
  }

  selectDay(event) {
    event.stopPropagation();
    const date = this.parseDate(event.currentTarget.dataset.date);

    // Clicking a grayed-out prev/next-month day (still a real, selectable
    // date -- only genuinely out-of-range days are non-interactive) also
    // moves the visible month to it, so the picker doesn't leave the user
    // looking at a just-picked date rendered as a faded "other month" cell
    // in a grid that's still showing the old month.
    if (date.getMonth() !== this.viewDate.getMonth() || date.getFullYear() !== this.viewDate.getFullYear()) {
      this.viewDate = new Date(date.getFullYear(), date.getMonth(), 1);
    }

    if (this.mode === "range") {
      if (!this.selectionStart || this.pickingEnd === false) {
        this.selectionStart = date;
        this.selectionEnd = null;
        this.pickingEnd = true;
      } else {
        if (date < this.selectionStart) {
          this.selectionEnd = this.selectionStart;
          this.selectionStart = date;
        } else {
          this.selectionEnd = date;
        }
        this.pickingEnd = false;
      }
    } else {
      this.selectionStart = date;
    }

    this.writeFields();
    this.updateInputText();
    this.render();

    if (this.mode === "single" || (this.mode === "range" && !this.pickingEnd)) this.close();
  }

  writeFields() {
    if (this.mode === "range") {
      if (this.hasOutboundFieldTarget && this.selectionStart) {
        this.outboundFieldTarget.value = this.formatISO(this.selectionStart);
        this.outboundFieldTarget.dispatchEvent(new Event("change", { bubbles: true }));
      }
      if (this.hasReturnFieldTarget && this.selectionEnd) {
        this.returnFieldTarget.value = this.formatISO(this.selectionEnd);
        this.returnFieldTarget.dispatchEvent(new Event("change", { bubbles: true }));
      }
    } else if (this.hasDateFieldTarget && this.selectionStart) {
      this.dateFieldTarget.value = this.formatISO(this.selectionStart);
      this.dateFieldTarget.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  updateInputText() {
    // inputTarget is a <button> (the popover trigger), not a form field --
    // its .value DOM property exists but doesn't affect rendered text, so
    // this must set textContent, not value.
    if (this.mode === "range") {
      if (this.selectionStart && this.selectionEnd) {
        this.inputTarget.textContent = `${this.formatDisplay(this.selectionStart)} - ${this.formatDisplay(this.selectionEnd)}`;
      } else if (this.selectionStart) {
        this.inputTarget.textContent = this.formatDisplay(this.selectionStart);
      }
    } else if (this.selectionStart) {
      this.inputTarget.textContent = this.formatDisplay(this.selectionStart);
    }
  }

  isDisabled(date) {
    if (this.minDateValue && date < this.parseDate(this.minDateValue)) return true;
    if (this.maxDateValue && date > this.parseDate(this.maxDateValue)) return true;
    return false;
  }

  isInRange(date) {
    if (this.mode !== "range" || !this.selectionStart || !this.selectionEnd) return false;
    return date > this.selectionStart && date < this.selectionEnd;
  }

  isSelected(date) {
    const iso = this.formatISO(date);
    return (
      (this.selectionStart && iso === this.formatISO(this.selectionStart)) ||
      (this.selectionEnd && iso === this.formatISO(this.selectionEnd))
    );
  }

  render() {
    const year = this.viewDate.getFullYear();
    const month = this.viewDate.getMonth();

    this.monthLabelTarget.textContent = new Intl.DateTimeFormat(this.localeValue, {
      month: "long",
      year: "numeric",
    }).format(this.viewDate);

    if (this.hasWeekdaysTarget && this.weekdaysTarget.childElementCount === 0) {
      // Monday-first weekday abbreviations, locale-aware.
      const formatter = new Intl.DateTimeFormat(this.localeValue, { weekday: "short" });
      const mondayRef = new Date(2024, 0, 1); // a known Monday
      const labels = Array.from({ length: 7 }, (_, i) => {
        const d = new Date(mondayRef);
        d.setDate(mondayRef.getDate() + i);
        return formatter.format(d);
      });
      this.weekdaysTarget.innerHTML = labels
        .map((label) => `<div class="flex h-10 items-center justify-center text-xs font-medium uppercase text-gray-400">${label}</div>`)
        .join("");
    }

    const firstOfMonth = new Date(year, month, 1);
    // Monday-first week grid.
    const startOffset = (firstOfMonth.getDay() + 6) % 7;
    const gridStart = new Date(year, month, 1 - startOffset);

    const cells = [];
    for (let i = 0; i < 42; i++) {
      const date = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i);
      const outOfMonth = date.getMonth() !== month;
      const disabled = this.isDisabled(date);
      const selected = this.isSelected(date);
      const inRange = this.isInRange(date);

      // rounded-full is applied only to selected/plain cells, never to
      // in-range cells -- Tailwind's fixed internal ordering for the
      // borderRadius utility group emits `rounded-none` BEFORE `rounded-full`
      // in the compiled stylesheet regardless of class-list order, so having
      // both present (as an earlier version of this code did, to try to
      // "unround" in-range cells) always loses to rounded-full. Simply never
      // adding rounded-full to in-range cells avoids the conflict and gives
      // the continuous pale-blue band Figma's design shows, instead of a
      // row of disconnected blue circles. In this picker every in-range day
      // is bounded on both ends by a selected (circular) day, never by a
      // plain one, so a flush rectangular edge butting up against the
      // circle is correct and matches the Figma reference exactly -- no
      // extra end-cap rounding needed.
      //
      // `border-0 appearance-none` is applied unconditionally to every cell
      // as a baseline, not styling -- without it, a plain <button> with no
      // explicit background/border class falls back to the browser's
      // native control chrome (a visible gray bevel/ring), which is exactly
      // what made every non-selected day look like a circle with an ugly
      // outline around it. `bg-transparent` is NOT added unconditionally
      // alongside it: that reintroduced the exact same class of bug as the
      // rounded-full/rounded-none conflict above -- with `important: true`
      // forcing every utility to `!important`, two conflicting background
      // utilities both present resolve by stylesheet order, not HTML class
      // order, and bg-transparent silently beat bg-primary/bg-primary-light,
      // making selected and in-range days render with invisible fills. It's
      // only added to the branches that don't set their own background.
      // outOfMonth is styling only (a faded prev/next-month day) -- it does
      // NOT imply non-interactive. Only a genuinely out-of-range day
      // (isDisabled, via minDate/maxDate) is actually unselectable. This
      // used to conflate the two, so every prev/next-month day was
      // unclickable regardless of whether the date itself was bookable --
      // the user's own explicit call: those days should be selectable,
      // same as any other real date, to make it easier to find a trip that
      // spans a month boundary without paging the calendar first.
      const classes = ["flex", "h-10", "w-10", "items-center", "justify-center", "border-0", "appearance-none", "text-sm", "transition-colors"];
      if (disabled) classes.push("bg-transparent", "text-gray-300", "cursor-not-allowed");
      else if (selected) classes.push("rounded-full", "bg-primary", "text-white", "font-semibold", "cursor-pointer");
      else if (inRange) classes.push("bg-primary-light", "text-primary", "cursor-pointer");
      else if (outOfMonth) classes.push("rounded-full", "bg-transparent", "text-gray-300", "hover:bg-primary-light", "hover:text-primary", "cursor-pointer");
      else classes.push("rounded-full", "bg-transparent", "text-gray-900", "hover:bg-primary-light", "cursor-pointer");

      const clickable = !disabled;
      const actionAttr = clickable ? `data-action="click->datepicker#selectDay" data-date="${this.formatISO(date)}"` : "";

      cells.push(`<div class="flex items-center justify-center"><button type="button" class="${classes.join(" ")}" ${actionAttr} ${clickable ? "" : "tabindex=\"-1\""}>${date.getDate()}</button></div>`);
    }

    this.gridTarget.innerHTML = cells.join("");
  }
}
